/**
 * Runtime configuration utility
 * This reads environment variables from the window._env_ object populated at container startup
 */

interface RuntimeEnv {
	DEVELOPMENT?: string;
	REACT_APP_API_ENDPOINT?: string;
	REACT_APP_API_KEY?: string;
	OKTA_ISSUER?: string;
	OKTA_CLIENT_ID?: string;
	OKTA_REDIRECT_URI?: string;
	OKTA_SCOPES?: string;
	MS_ENTRA_TENANT_ID?: string;
	MS_ENTRA_CLIENT_ID?: string;
	MS_ENTRA_AUTHORITY?: string;
	MS_ENTRA_REDIRECT_URI?: string;
	MS_ENTRA_SCOPES?: string;
	NODE_ENV?: string;
	CLIENT_SPECIFIC?: string;
}

declare global {
	interface Window {
		_env_?: RuntimeEnv;
	}
}

/**
 * Build-time environment variables
 * These are replaced by Umi's define config at build time
 * We need to access them directly (not via dynamic key) for replacement to work
 */
const buildTimeEnv: RuntimeEnv = {
	NODE_ENV: process.env.NODE_ENV,
	DEVELOPMENT: process.env.DEVELOPMENT,
	REACT_APP_API_ENDPOINT: process.env.REACT_APP_API_ENDPOINT,
	REACT_APP_API_KEY: process.env.REACT_APP_API_KEY,
	OKTA_ISSUER: process.env.OKTA_ISSUER,
	OKTA_CLIENT_ID: process.env.OKTA_CLIENT_ID,
	OKTA_REDIRECT_URI: process.env.OKTA_REDIRECT_URI,
	OKTA_SCOPES: process.env.OKTA_SCOPES,
	MS_ENTRA_TENANT_ID: process.env.MS_ENTRA_TENANT_ID,
	MS_ENTRA_CLIENT_ID: process.env.MS_ENTRA_CLIENT_ID,
	MS_ENTRA_AUTHORITY: process.env.MS_ENTRA_AUTHORITY,
	MS_ENTRA_REDIRECT_URI: process.env.MS_ENTRA_REDIRECT_URI,
	MS_ENTRA_SCOPES: process.env.MS_ENTRA_SCOPES,
	CLIENT_SPECIFIC: process.env.CLIENT_SPECIFIC,
};

/**
 * Get environment variable value
 * Fallback order: window._env_ (runtime) -> process.env (build-time) -> default
 */
export const getEnv = (key: keyof RuntimeEnv, defaultValue = ''): string => {
	// First try runtime config (only if it has actual values)
	if (typeof window !== 'undefined' && window._env_) {
		const value = window._env_[key];
		// If value exists, is not empty, and is not a template placeholder, use it
		if (value && value !== '' && !value.startsWith('${')) {
			return value;
		}
	}

	// Fallback to build-time process.env (replaced by Umi's define config)
	const processEnvValue = buildTimeEnv[key];
	if (processEnvValue !== undefined && processEnvValue !== 'undefined') {
		return processEnvValue;
	}

	// Return default value
	return defaultValue;
};

/**
 * Get boolean environment variable
 */
export const getEnvBoolean = (
	key: keyof RuntimeEnv,
	defaultValue = false,
): boolean => {
	const value = getEnv(key, String(defaultValue));
	return value === 'true' || value === '1';
};

/**
 * Check if we're running in development mode
 */
export const isDevelopment = (): boolean => {
	return getEnv('NODE_ENV') === 'development';
};

/**
 * Check if development features are enabled
 */
export const isDevelopmentEnabled = (): boolean => {
	return getEnvBoolean('DEVELOPMENT', false);
};

// Export commonly used values as getters to ensure they're always read fresh
export const runtimeConfig = {
	get isDev() {
		return isDevelopment();
	},
	get isDevelopmentEnabled() {
		return isDevelopmentEnabled();
	},
	get apiEndpoint() {
		const api_end_point = getEnv(
			'REACT_APP_API_ENDPOINT',
			'http://localhost:8090',
		);
		return api_end_point;
	},
	get apiKey() {
		return getEnv('REACT_APP_API_KEY', '');
	},
	get okta() {
		return {
			get issuer() {
				return getEnv('OKTA_ISSUER', '');
			},
			get clientId() {
				return getEnv('OKTA_CLIENT_ID', '');
			},
			get redirectUri() {
				return getEnv('OKTA_REDIRECT_URI', '');
			},
			get scopes() {
				return getEnv('OKTA_SCOPES', 'openid profile email');
			},
		};
	},
	get msEntra() {
		return {
			get clientId() {
				return getEnv('MS_ENTRA_CLIENT_ID', '');
			},
			get tenantId() {
				return getEnv('MS_ENTRA_TENANT_ID', '');
			},
			get authority() {
				return getEnv('MS_ENTRA_AUTHORITY', '');
			},
			get redirectUri() {
				return getEnv('MS_ENTRA_REDIRECT_URI', '');
			},
			get scopes() {
				return getEnv('MS_ENTRA_SCOPES', 'openid profile email');
			},
		};
	},
	get clientSpecific() {
		return getEnv('CLIENT_SPECIFIC', '');
	},
	get isFedEx() {
		return getEnv('CLIENT_SPECIFIC', '').toLowerCase() === 'fedex';
	},
};

export default runtimeConfig;
